/**
 * Password Policy Validation
 *
 * Enforces a combination of complexity, character restrictions,
 * sequence detection rules, and user identity validation.
 */

/**
 * @typedef {{ key: string; ok: boolean; detail: string; }} PasswordValidationRule
 */

export interface PasswordValidationRule {
  key: string;
  ok: boolean;
  detail: string;
}

/**
 * Checks whether the password contains the user's email address or username.
 *
 * Strips separator characters (`.`, `_`, `-`) from the username and compares
 * normalised versions case-insensitively.
 *
 * @param {string} password
 * @param {string} email
 * @returns {boolean}
 */
export function containsEmailReference(password: string, email: string): boolean {
  if (!email) {
    return false;
  }

  const passwordLower = password.toLowerCase();
  const emailLower = email.toLowerCase();

  const username = emailLower.split('@')[0];

  // Remove non-alphanumeric characters for normalised comparison
  const normalizedUsername = username.replace(/[^a-z0-9]/g, '');
  const normalizedPassword = passwordLower.replace(/[^a-z0-9]/g, '');

  return (
    passwordLower.includes(emailLower) ||
    (normalizedUsername.length > 0 && normalizedPassword.includes(normalizedUsername))
  );
}

/**
 * Validates a password against the full RAE password policy.
 *
 * Returns an array of rule results — each entry has:
 *   - `key`    — unique rule identifier
 *   - `ok`     — whether the rule passed
 *   - `detail` — human-readable description shown to the user
 *
 * @param {string} password
 * @param {string} email
 * @returns {PasswordValidationRule[]}
 */
export function validatePassword(password: string, email: string): PasswordValidationRule[] {
  return [
    {
      key: 'length_10_40',
      ok: /^.{10,40}$/.test(password),
      detail: 'Length must be between 10 and 40 characters',
    },
    {
      key: 'no_whitespace',
      ok: /^\S+$/.test(password),
      detail: 'No whitespace allowed',
    },
    {
      key: 'has_number',
      ok: /[0-9]/.test(password),
      detail: 'Must include at least one number',
    },
    {
      key: 'has_upper_and_lower',
      ok: /[A-Z]/.test(password) && /[a-z]/.test(password),
      detail: 'Must include uppercase and lowercase letters',
    },
    {
      key: 'has_symbol',
      ok: /[~!@#$%^&+=*()\-_?'":;,.<>{}\[\]£\/\\]/.test(password),
      detail: 'Must include at least one symbol',
    },
    {
      key: 'allowed_characters',
      ok: /^[a-zA-Z0-9~!@#$%^&+=*()\-_?'":;,.<>{}\[\]£\/\\]+$/.test(password),
      detail: 'Must contain only supported characters',
    },
    {
      key: 'no_email_address',
      ok: !containsEmailReference(password, email),
      detail: 'Password cannot contain your email address or username',
    },
    {
      key: 'no_triple_repeats',
      ok: !/([a-zA-Z0-9~!@#$%^&+=*()\-_?'":;,.<>{}\[\]£\/\\])\1{2,}/.test(password),
      detail: 'Cannot contain 3 consecutive identical characters',
    },
    {
      key: 'no_ascending_letters',
      ok: !/(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i.test(
        password,
      ),
      detail: 'Cannot contain ascending letter sequences',
    },
    {
      key: 'no_ascending_numbers',
      ok: !/(123|234|345|456|567|678|789)/.test(password),
      detail: 'Cannot contain ascending number sequences',
    },
    {
      key: 'no_descending_numbers',
      ok: !/(321|432|543|654|765|876|987)/.test(password),
      detail: 'Cannot contain descending number sequences',
    },
    {
      key: 'no_repeated_number_triplets',
      ok: !/(111|222|333|444|555|666|777|888|999|000)/.test(password),
      detail: 'Cannot contain repeated number triplets',
    },
  ];
}

/**
 * Returns only the password-policy rules that failed.
 *
 * @param {string} password
 * @param {string} email
 * @returns {PasswordValidationRule[]}
 */
export function getFailedPasswordPolicyRules(password: string, email: string): PasswordValidationRule[] {
  return validatePassword(password, email).filter((rule) => !rule.ok);
}

/**
 * Returns `true` when ALL password policy rules pass.
 *
 * @param {string} password
 * @param {string} email
 * @returns {boolean}
 */
export function isPasswordPolicyCompliant(password: string, email: string): boolean {
  return getFailedPasswordPolicyRules(password, email).length === 0;
}
